import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal, { inputCls, btnPrimary, btnGhost } from "@/components/Modal";
import { Plus, Trash2, ArrowRightLeft, LayoutGrid } from "lucide-react";

const STATUS = {
  available: { label: "Available", cls: "border-[#10B981] bg-[#10B981]/10 text-[#10B981]", dot: "bg-[#10B981]" },
  occupied: { label: "Occupied", cls: "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]", dot: "bg-[#F59E0B]" },
  bill_requested: { label: "Bill Requested", cls: "border-[#A855F7] bg-[#A855F7]/10 text-[#A855F7]", dot: "bg-[#A855F7]" },
};

export default function Tables() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveFrom, setMoveFrom] = useState(null);
  const [moveTo, setMoveTo] = useState("");

  const load = async () => {
    const { data } = await api.get("/tables");
    setTables(data);
  };
  useEffect(() => {
    load();
  }, []);

  const addTable = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/tables", { name });
      setOpen(false);
      setName("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    await api.delete(`/tables/${t.id}`);
    load();
  };

  const clickTable = (t) => {
    if (t.status !== "available" && t.current_bill_id) {
      navigate(`/pos?bill=${t.current_bill_id}`);
    } else {
      navigate(`/pos?table=${t.id}`);
    }
  };

  const cycleStatus = async (t) => {
    const order = ["available", "occupied", "bill_requested"];
    const next = order[(order.indexOf(t.status) + 1) % order.length];
    await api.put(`/tables/${t.id}/status?status=${next}`);
    load();
  };

  const doMove = async () => {
    if (!moveTo) return;
    try {
      await api.post(`/tables/move?from_id=${moveFrom.id}&to_id=${moveTo}`);
      toast.success("Order moved");
      setMoveOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold">Tables</h1>
          <p className="text-sm text-[#6B7280]">Tap an occupied table to open its order</p>
        </div>
        {user?.role === "admin" && (
          <button data-testid="add-table-btn" onClick={() => setOpen(true)} className={btnPrimary + " flex items-center gap-2"}>
            <Plus className="w-4 h-4" /> Add Table
          </button>
        )}
      </div>

      <div className="flex gap-4 mb-5 text-xs">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[#9CA3AF]">
            <span className={`w-2.5 h-2.5 rounded-full ${v.dot}`} /> {v.label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {tables.map((t) => {
          const st = STATUS[t.status] || STATUS.available;
          return (
            <div key={t.id} data-testid={`table-${t.id}`} className={`relative rounded-xl border-2 ${st.cls} p-4 cursor-pointer transition-transform hover:-translate-y-0.5`} onClick={() => clickTable(t)}>
              <LayoutGrid className="w-5 h-5 mb-6 opacity-70" />
              <div className="font-head font-bold text-lg text-white">{t.name}</div>
              <div className="text-xs mt-1">{st.label}</div>
              <div className="absolute top-2 right-2 flex gap-1">
                <button onClick={(e) => { e.stopPropagation(); cycleStatus(t); }} title="Change status" className="p-1 text-current opacity-60 hover:opacity-100 text-[10px]">⟳</button>
                {t.status !== "available" && (
                  <button onClick={(e) => { e.stopPropagation(); setMoveFrom(t); setMoveTo(""); setMoveOpen(true); }} title="Move order" className="p-1 opacity-60 hover:opacity-100"><ArrowRightLeft className="w-3.5 h-3.5" /></button>
                )}
                {user?.role === "admin" && (
                  <button onClick={(e) => { e.stopPropagation(); del(t); }} className="p-1 opacity-60 hover:opacity-100 hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Table" footer={<><button onClick={() => setOpen(false)} className={btnGhost}>Cancel</button><button onClick={addTable} data-testid="save-table-btn" className={btnPrimary}>Add</button></>}>
        <input className={inputCls} placeholder="Table name e.g. Table 13" value={name} data-testid="table-name-input" onChange={(e) => setName(e.target.value)} autoFocus />
      </Modal>

      <Modal open={moveOpen} onClose={() => setMoveOpen(false)} title={`Move order from ${moveFrom?.name}`} footer={<><button onClick={() => setMoveOpen(false)} className={btnGhost}>Cancel</button><button onClick={doMove} className={btnPrimary}>Move</button></>}>
        <select className={inputCls} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
          <option value="">Select destination table…</option>
          {tables.filter((t) => t.id !== moveFrom?.id && t.status === "available").map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Modal>
    </div>
  );
}
